import type { SerialAndModbusConfig } from '@/features/controller/configuration/models/controllerConfigTree'
import type { ControllerConfigurationModel } from '@/features/controller/configuration/models/controllerConfiguration'
import {
  modbusDataTypeAddressStep,
  threePhaseActivePowerRegisterAddresses,
} from '@/features/controller/configuration/models/raspiProperties/modbus'
import { getSerialPortName } from '@/features/controller/configuration/models/raspiProperties/serialPort'
import type {
  LegacyModbusConnection,
  LegacyModbusMeter,
  LegacyModbusTcpConnection,
  ModbusRegister,
  ModbusTcpHostConfig,
  NamedModbusConnection,
  NamedModbusSerialClient,
} from '@/features/controller/configuration/models/raspiProperties/types'
import {
  ModbusDataType,
  ModbusPowerRegisterVariant,
  NamedModbusTransport,
  WordOrder,
} from '@/features/controller/configuration/models/raspiProperties/types'
import type { ControllerDescription } from '@/features/controller/types'
import { renderControllerName } from '@/features/controller/utils/controllerNames'

export type ModbusTestingTarget = SerialAndModbusConfig | ModbusTcpHostConfig | NamedModbusSerialClient

type ModbusSupportedDataType = ModbusDataType | 'INT64'

type ModbusParams = Pick<LegacyModbusMeter, 'unitId' | 'registerAddressOffset' | 'wordOrder'>

function getModbusSupportedDataTypeAddressStep(dataType: ModbusSupportedDataType) {
  return dataType === 'INT64' ? 4 : modbusDataTypeAddressStep(dataType)
}

function generateTestCommand(
  target: ModbusTestingTarget,
  { unitId, registerAddressOffset, wordOrder }: ModbusParams,
  register: ModbusRegister,
  dataType: ModbusSupportedDataType,
) {
  let dataTypeOpt: string
  switch (dataType) {
    case ModbusDataType.INT16:
      dataTypeOpt = 'uint16'
      break
    case ModbusDataType.INT32:
      dataTypeOpt = 'uint16'
      break
    case 'INT64':
      dataTypeOpt = 'uint16'
      break
    case ModbusDataType.FLOAT:
      if (wordOrder === WordOrder.BIG_ENDIAN) {
        dataTypeOpt = 'float32-big-endian'
      } else {
        dataTypeOpt = 'float32-little-endian'
      }
      break
  }
  const address = register.address + registerAddressOffset
  const registerCount = getModbusSupportedDataTypeAddressStep(dataType)
  if (target['legacySerialPortConfig'] || target['portConfig']) {
    // A named serial connection carries its own framing; the legacy one takes the controller's
    const { portConfig, serialPort } = target['portConfig']
      ? (target as NamedModbusSerialClient)
      : (target as SerialAndModbusConfig).legacySerialPortConfig
    const port = getSerialPortName(serialPort)
    const { baudRate, parity, stopBits } = portConfig
    const stopBitsOpt = stopBits === 2 ? 'two' : 'one'
    return `conman modbus rtu --serial-port ${port} --baud-rate ${baudRate} --parity ${parity} --stop-bits ${stopBitsOpt} ${unitId} ${address} ${registerCount} ${dataTypeOpt} ${register.registers}`
  } else {
    const t = target as ModbusTcpHostConfig
    return `conman modbus tcp --port ${t.port} ${t.host} --device-address ${unitId} ${address} ${registerCount} ${dataTypeOpt} ${register.registers}`
  }
}

interface RegisterTest {
  label: string
  register: ModbusRegister
  dataType: ModbusSupportedDataType
  scale: number
}

function generateScriptForRegisters(
  modbusTestingTarget: ModbusTestingTarget,
  modbusParams: ModbusParams,
  registers: RegisterTest[],
) {
  return registers
    .map((test) => {
      const scaleNote = test.scale !== 1 ? `, scale result by ${test.scale}` : ''
      const isDataTypeNotHandledByConman = test.dataType === 'INT64' || test.dataType === ModbusDataType.INT32
      const wordOrderNote = isDataTypeNotHandledByConman ? `, ${modbusParams.wordOrder} word order` : ''

      return (
        `echo "${test.label} (${test.dataType})${wordOrderNote}${scaleNote}:"\n` +
        generateTestCommand(modbusTestingTarget, modbusParams, test.register, test.dataType)
      )
    })
    .join('\n')
}

function generateMeterTestingScript(modbusTestingTarget: ModbusTestingTarget, meter: LegacyModbusMeter) {
  const registersToTest: RegisterTest[] = []

  if (meter.activePowerRegisters) {
    if (meter.activePowerRegisters.variant === ModbusPowerRegisterVariant.threePhase) {
      const { phase1, phase2, phase3 } = threePhaseActivePowerRegisterAddresses(
        meter.activePowerRegisters.address,
        meter.powerRegistersDataType,
      )
      registersToTest.push(
        {
          label: 'Active power phase 1',
          register: { address: phase1, registers: meter.activePowerRegisters.registers },
          dataType: meter.powerRegistersDataType,
          scale: meter.activePowerScale,
        },
        {
          label: 'Active power phase 2',
          register: { address: phase2, registers: meter.activePowerRegisters.registers },
          dataType: meter.powerRegistersDataType,
          scale: meter.activePowerScale,
        },
        {
          label: 'Active power phase 3',
          register: { address: phase3, registers: meter.activePowerRegisters.registers },
          dataType: meter.powerRegistersDataType,
          scale: meter.activePowerScale,
        },
      )
    } else {
      registersToTest.push({
        label: 'Active power total',
        register: meter.activePowerRegisters,
        dataType: meter.powerRegistersDataType,
        scale: meter.activePowerScale,
      })
    }
  }

  if (meter.reactivePowerRegister) {
    registersToTest.push({
      label: 'Reactive power',
      register: meter.reactivePowerRegister,
      dataType: meter.powerRegistersDataType,
      scale: meter.reactivePowerScale,
    })
  }
  if (meter.watchdogRegister) {
    registersToTest.push({
      label: 'Watchdog',
      register: meter.watchdogRegister,
      dataType: ModbusDataType.INT16,
      scale: 1,
    })
  }
  if (meter.meterErrorRegister) {
    registersToTest.push({
      label: 'Meter error',
      register: meter.meterErrorRegister,
      dataType: ModbusDataType.INT16,
      scale: 1,
    })
  }
  if (meter.systemTimestampRegister) {
    registersToTest.push({
      label: 'System timestamp',
      register: meter.systemTimestampRegister,
      dataType: 'INT64',
      scale: 1,
    })
  }
  if (meter.dataTimestampRegister) {
    registersToTest.push({
      label: 'Data timestamp',
      register: meter.dataTimestampRegister,
      dataType: 'INT64',
      scale: 1,
    })
  }

  if (registersToTest.length > 0) {
    return (
      `echo "Test for Modbus Meter #${meter.number}"\n` +
      generateScriptForRegisters(modbusTestingTarget, meter, registersToTest) +
      `\necho "-----"`
    )
  } else {
    return `echo "No registers to test for Modbus Meter #${meter.number}"`
  }
}

/**
 * Resolve where a meter is actually reached. A meter on a named connection has no endpoint of its
 * own, so it has to be taken from the connection it references.
 */
function getModbusTestingTarget(
  conn: LegacyModbusConnection,
  raspiPropertiesConfig: SerialAndModbusConfig,
): ModbusTestingTarget {
  if (conn['serialPort']) {
    return raspiPropertiesConfig
  }
  if (conn['namedClient']) {
    const namedClient = (conn as NamedModbusConnection).namedClient
    return namedClient.transport === NamedModbusTransport.tcp
      ? { host: namedClient.address, port: namedClient.port }
      : namedClient
  }
  return (conn as LegacyModbusTcpConnection).tcpTarget
}

export function generateModbusTestingScript(
  controllerDescription: ControllerDescription,
  configurationState: ControllerConfigurationModel,
) {
  const raspiPropertiesConfig = configurationState.raspiPropertiesConfig
  if (raspiPropertiesConfig) {
    const { legacyModbusConnections } = raspiPropertiesConfig
    const meterScripts: string[] = []
    for (const conn of legacyModbusConnections) {
      const testingTarget = getModbusTestingTarget(conn, raspiPropertiesConfig)
      for (const meter of conn.meters) {
        meterScripts.push(generateMeterTestingScript(testingTarget, meter))
      }
    }
    if (meterScripts.length > 0) {
      return `echo "Modbus test for ${renderControllerName(controllerDescription)}"\n\n` + meterScripts.join('\n\n')
    }
  }

  return `# No supported Modbus configurations found`
}
